import React, { useEffect, useRef, useState } from "react";
import { EBrandSelectType } from "@Types/constants";
import { useQuery } from "react-query";
import { CaretDownOutlined, CaretUpOutlined, RightOutlined } from "@ant-design/icons";
import { EPageType, IBrand, IModel } from "@Types/specification";
import { useDebounceFn, useThrottle, useThrottleFn } from "ahooks";
import { ICommonKindData } from "@Types/common";
import { Skeleton } from "antd";
import { useAtom } from "jotai";
import { pkComparisonAtom } from "@Lib/jotai/pkCompare";
import { initTracking } from "@Lib/jotai/tracking";
import { fetchV5BrandSelect, fetchV5KindsSelect, fetchV5ModelSelect } from "@Services/common";

interface IProps {
  SelectType?: string;
  selectBrandId?: number;
  selectKindId?: number;
  title?: string;
  pageType?: EPageType;
  style?: React.CSSProperties;
  modelStyle?: React.CSSProperties;
  customSelect?: React.ReactNode;
  // 禁用车型
  disabledModels?: string[] | number[];
  // 添加完车型后是否走全局上报
  updataGaGloball?: boolean;
  // 添加车款时是否需要记录全局
  addKindsSetLocal?: boolean;
  // 滚动时是否需要隐藏
  scrollNeeddVisible?: boolean;
  // 選擇車型
  onSelectModel?: (data: any) => void;
  // 更新visible
  updateVisible?: (visible: boolean) => void

}

const BrandSelect: React.FC<{ brandData: IBrand, handleClickBrandId: (id: number) => void, handleClickBrandName: (name: string) => void }> = ({ brandData, handleClickBrandId, handleClickBrandName }) => {
  if (!brandData || !brandData.index || !brandData.brand) return <div className="p-10"><Skeleton /></div>;
  const { index, brand }: any = brandData || {};
  const [activeLetter, setActiveLetter] = useState<string>('');
  // 用来存储每个右侧区域的引用
  const storeRefs = useRef({});
  // 右侧滚动容器的引用
  const rightSideRef = useRef<HTMLDivElement>(null);
  // 禁止滚动事件更新
  const isScrolling = useRef(false);

  // 滚动到指定锚点
  const handleClick = (label) => {
    setActiveLetter(label);
    isScrolling.current = true;
    const container = rightSideRef.current;
    const target = document.getElementById(label);

    if (container && target) {
      container.scrollTop = target.offsetTop - container.offsetTop;
    }
  };

  // 监听右侧区域滚动，更新左侧选中的字母
  const { run: handleScroll } = useThrottleFn(() => {
    if (isScrolling.current) {
      isScrolling.current = false;
      // 禁用滚动事件时，跳过更新
      return;
    }

    for (const letter of index) {
      const storeElement = storeRefs.current[letter];
      if (storeElement) {
        const rect = storeElement.getBoundingClientRect();
        if (rect.top >= 0 && rect.top <= window.innerHeight / 2) {
          setActiveLetter(letter);
          break;
        }
      }
    }
  }, { wait: 500 });

  // 添加滚动事件监听
  useEffect(() => {
    if (!index.length) return;
    const rightSideElement = rightSideRef.current;
    if (rightSideElement) {
      rightSideElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (rightSideElement) {
        rightSideElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [index]);


  useEffect(() => {
    !!index.length && setActiveLetter(index[0]);
  }, [index]);

  return (
    <div className="flex h-[522px]">
      {/* 左侧字母 */}
      <div className="w-24 flex flex-col items-center gap-10 pt-6 pb-14 border-0 border-r-1 border-solid border-[#EDEFF5]">
        {!!index.length && index.map((letter) => (
          <div
            key={letter}
            className={`w-18 h-18 flex items-center justify-center cursor-pointer ${activeLetter === letter ? 'bg-[#406EFF] text-[#fff] rounded-4' : 'text-[#1D2029]'}`}
            onClick={() => {
              handleClick(letter);
            }}
          >
            {letter}
          </div>
        ))}
      </div>

      {/* 右侧数据 */}
      <div className="flex-1 overflow-y-auto" ref={rightSideRef} >
        {!!brand.length && brand.map((store) => (
          <div key={store.label} ref={(el) => { if (el) storeRefs.current[store.label] = el; }} id={store.label} className="flex flex-col ">
            <div className="bg-[#F5F6FA] border-0 border-b-1 border-t-1 border-solid border-[#EDEFF5] h-40 flex items-center justify-strat pl-16 text-[#333333] text-14">{store.label}</div>
            {store.list.length > 0 && (
              store.list.map(item => (
                <div key={item.id} className="flex items-center justify-start h-40 pl-16 cursor-pointer hover:bg-[#E0EFFF]" onClick={() => {
                  handleClickBrandId(item.id!);
                  handleClickBrandName(item.enName!);
                }}>
                  <img src={item.image} alt={item.name} className="w-20 h-20 mr-10" />
                  <div className="text-[#1D2029] text-14">{`${item.zhName}${item.zhName ? `/${item.enName}` : `${item.enName}`}`}</div>
                </div>
              ))
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const KindSelect: React.FC<{ kindData: ICommonKindData[], handleClickKind: (id: number) => void, handleClickKindName: (name: string) => void }> = ({ kindData, handleClickKind, handleClickKindName }) => {
  if (!Array.isArray(kindData) || !kindData.length) return <div className="p-10"><Skeleton /></div>;
  return (
    <div className="flex max-h-[486px]">
      <div className="flex-1 overflow-y-auto flex-col" >
        {kindData.map((item) => {
          const priceText = item.price === '暫無報價' ? '暫無報價' : String(item.price)?.includes('萬') ? item.price : `${item.price}萬`;
          const priceClass = item.price === '暫無報價' ? 'text-[#656873]' : 'text-[#F87F15] font-[600]';

          return (
            <div key={item.kindId} className="flex items-center justify-between py-12 pl-16 cursor-pointer hover:bg-[#E0EFFF] text-14" onClick={() => {
              handleClickKind(item.kindId);
              handleClickKindName(item.kindName);
            }}>
              <div className="text-[#1D2029] text-14 w-[162px]">{item.kindName}</div>
              <div className={`pr-16 ${priceClass}`}>{priceText}</div>
            </div>
          )
        })}
      </div>
    </div>
  );
};

const ModelSelect: React.FC<{ modelData: IModel[], handleClickModel: (data: any) => void, disabledModels: any[] }> = ({ modelData, disabledModels, handleClickModel }) => {
  if (!Array.isArray(modelData) || !modelData.length) return <div className="p-10"><Skeleton /></div>;

  const disabledModel = new Set(disabledModels);

  return (
    <div className="flex max-h-[486px]">
      <div className="flex-1 overflow-y-auto flex-col" >
        {modelData.map((item) => {
          const priceText = !!item.price ? `${item.price}萬` : '暫無報價';
          const priceClass = !!item.price ? 'text-[#F87F15] font-[600]' : 'text-[#656873]';
          const modelName = `${item.year}年式 ${item.modelName}`;

          return (
            <div
              key={item.modelYearId}
              className={`flex items-center justify-between py-12 pl-16 text-14 ${disabledModel.has(item.modelYearId) ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-[#E0EFFF]'}`}
              onClick={() => {
                disabledModel.has(item.modelYearId) ? null : handleClickModel({ modelName: modelName, myid: item.modelYearId, thumb: item.thumb, price: priceText });
              }}
            >
              <div className={`w-202 ${disabledModel.has(item.modelYearId) ? 'text-[#939BAF]' : 'text-[#1D2029]'}`}>{modelName}</div>
              <div className={`pr-16 ${priceClass}`}>{priceText}</div>
            </div>
          )
        })}
      </div>
    </div>
  );
};


const BrandKindModelSelect: React.FC<IProps> = ({
  title,
  style,
  customSelect,
  scrollNeeddVisible,
  modelStyle,
  SelectType,
  pageType,
  selectBrandId,
  selectKindId,
  disabledModels,
  addKindsSetLocal = true,
  updataGaGloball = true,
  onSelectModel,
  updateVisible
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [brandId, setBrandId] = useState<number | undefined>(undefined);
  const [brandName, setBranamedName] = useState<string | undefined>(undefined);
  const [kindId, setKindId] = useState<number | undefined>(undefined);
  const [kindName, setKindName] = useState<string | undefined>(undefined);
  const [visible, setVisible] = useState<boolean>(false);
  const [scrollX, setScrollX] = useState(0);
  const [lastScrollX, setLastScrollX] = useState(0); // 存储上一次的scrollX
  const [globalPkList, updateGlobalPkList] = useAtom(pkComparisonAtom);
  const [tracking] = useAtom(initTracking);
  const [initStatus, setInitStatus] = useState<boolean>(true); // 是否初始化厂牌、车款

  const throttledScrollX = useThrottle(scrollX, { wait: 50 });

  const handleScroll = () => {
    const currentScrollX = window.scrollX;
    if (currentScrollX !== lastScrollX) { // 只有在scrollX变化时才更新
      setScrollX(currentScrollX);
      setLastScrollX(currentScrollX);
    }
  };

  // 优化滚动事件
  const handleScrollWithRaf = () => {
    !!scrollNeeddVisible && requestAnimationFrame(handleScroll);
  };

  // 獲取廠牌數據
  const { data: brandData } = useQuery("fetchV5BrandSelect", async () => {
    const res: { status, data } = await fetchV5BrandSelect({ type: EBrandSelectType.BASE });
    if (res.status === 12000 && res.data) {
      return {
        index: res.data.list.map(item => item.label),
        brand: res.data.list,
      };
    }
    return { brand: [], index: [] };
  });

  // 獲取车款數據
  const { data: kindData } = useQuery(
    ["fetchV5KindsSelect", brandId],
    async () => {
      const res: { status, data } = await fetchV5KindsSelect({
        type: EBrandSelectType.BASE,
        brandId: Number(brandId),
      });
      if (res.status === 12000 && res.data) {
        return res.data.list;
      }
      return [];
    },
    {
      enabled: !!brandId,
    },
  );

  // 獲取車型數據
  const { data: modelData } = useQuery(
    ["fetchV5ModelSelect", kindId],
    async () => {
      const res: { status, data } = await fetchV5ModelSelect({
        type: EBrandSelectType.BASE,
        kindId: Number(kindId),
      });
      if (res.status === 12000 && res.data) {
        return res.data.list;
      }

      return [];
    },
    {
      enabled: !!kindId,
    },
  );

  const reset = () => {
    setBrandId(undefined);
    setBranamedName(undefined);
    setKindId(undefined);
    setKindName(undefined);
    setVisible(false);
  }

  // 全局添加对比车型
  const setCompeteListValue = (data) => {
    addKindsSetLocal && updateGlobalPkList({
      data,
      callback: (data) => { },
      type: SelectType
    });
    reset();
    onSelectModel?.(data);

    updataGaGloball && tracking && tracking.trackEvent({
      name: "element_clicked",
      params: {
        page_business: "新車",
        trigger_id: "4027",
        page_id: "714",
        trigger_name: "添加對比車型",
        object_type: "myid",
        next_spm: "3.714.809.2212",
        page: "新車添加比較彈窗頁",
        channel: "新車",
        element_id: "添加對比車型",
        element: "按鈕",
        module: "添加比較彈窗",
        tracker_method: "config",
        object_id: data.myid || '',
      }
    });
  };

  const handleClickOutside = (event) => {
    if (drawerRef.current && !(drawerRef.current as HTMLElement).contains(event.target as HTMLElement)) {
      reset();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener("scroll", handleScrollWithRaf);

    reset();

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener("scroll", handleScrollWithRaf);
    };
  }, []);

  // 判断是否已有厂牌，并跳过选择厂牌的步骤
  const setSelectBrandId = async (): Promise<boolean> => {
    const newBrandId = Number(selectBrandId);
    if (!newBrandId || !brandData?.brand.length) {
      return false;
    }

    setBrandId(newBrandId);

    try {
      const flattenedBrands = (brandData.brand as any).flatMap(group => group.list);
      const matchedBrand = flattenedBrands.find(
        brand => Number(brand?.id) === newBrandId
      );

      if (matchedBrand) {
        setBranamedName(matchedBrand.enName);
        return true;
      }
    } catch (error) {
      console.error('返選設置廠牌失敗:', error);
    }

    return false;
  }

  // 判断是否已有车款，并跳过选择车款的步骤
  const setSelectKindId = (): boolean => {
    const newKindId = Number(selectKindId);

    if (!newKindId || !kindData?.length) {
      return false;
    }

    setKindId(newKindId);

    try {
      const matchedBrand = kindData.find(
        kind => Number(kind?.kindId) === newKindId
      );

      if (matchedBrand) {
        setKindName(matchedBrand.kindName);
        return true;
      }
    } catch (error) {
      console.error('返選設置廠牌失敗:', error);
    }

    return false;
  }

  useEffect(() => {
    updateVisible?.(visible);
    setInitStatus(true);
  }, [visible]);

  useEffect(() => {
    setVisible(false);
  }, [throttledScrollX]);


  useEffect(() => {
    if (!tracking) return;
    pageType === EPageType.SPEC && tracking.trackEvent({
      "name": "element_exposured",
      "params": {
        "page_sub_business": "新車資料庫",
        "page_business": "新車",
        "trigger_id": "4079",
        "page_id": "622",
        "trigger_name": "更換車型",
        "next_spm": "3.622.810.2170",
        "page": "PK比較結果頁",
        "channel": "新車",
        "element_id": "更換車型",
        "element": "按鈕",
        "module": "車型列表",
        "tracker_method": "config"
      }
    });

    pageType === EPageType.SUMMARY && tracking.trackEvent({
      "name": "element_exposured",
      "params": {
        "page_business": "新車",
        "trigger_id": "4126",
        "page_id": "721",
        "trigger_name": "更換車型",
        "next_spm": "3.721.859.2326",
        "page": "PK比較綜合比較頁",
        "channel": "新車",
        "element_id": "更換車型",
        "element": "按鈕",
        "module": "車型列表",
        "tracker_method": "config"
      }
    });
  }, [tracking, pageType]);

  const handleClick = async () => {
    await setSelectBrandId();
    setTimeout(() => {
      setVisible(!visible);
    }, 300);
  };

  useEffect(() => {
    if (!!selectKindId && initStatus) {
      Promise.resolve(setSelectKindId());
    }
  }, [selectKindId, kindData]);

  return (
    <section
      ref={drawerRef}
      style={style}
      className={`relative cursor-pointer ${customSelect ? '' : 'w-[314px] h-32 text-[#939BAF] mx-auto px-10 text-14 flex items-center bg-[#fff] border-1 border-solid border-[#9DA2AF] mt-14 rounded-4'}`}
    >
      {customSelect
        ? <div
          id="tracking-CommonBrandKindSelect"
          onClick={handleClick}
        >{customSelect}</div>
        : <div
          id="tracking-CommonBrandKindSelect"
          className={`flex items-center justify-between w-full h-full`}
          onClick={handleClick}
        >
          <p className="text-[#1D2029]">{title}</p>

          {!visible ? (
            <CaretDownOutlined style={{ fontSize: "12px", color: "#666" }} />
          ) : (
            <CaretUpOutlined style={{ fontSize: "12px", color: "#666" }} />
          )}
        </div>}

      {visible && <div className="absolute left-0 top-38 my-auto w-[314px] rounded-4 bg-[#fff] z-[12]" style={{ ...modelStyle, boxShadow: '0px 2px 18px 0px rgba(0,0,0,0.20)' }}>

        <p className="text-[#333333] flex items-center justify-start flex-1 h-40 pl-16 text-14">
          <span className="flex items-center max-w-[124px] whitespace-nowrap cursor-pointer" onClick={() => {
            setBrandId(undefined);
            setBranamedName(undefined);
            setKindId(undefined);
            setKindName(undefined);
          }}>
            {brandName || '選擇廠牌'}
          </span>
          {!!brandName && <span className="line-clamp-1 cursor-pointer" onClick={() => {
            setKindId(undefined);
            setKindName(undefined);
          }}>
            <RightOutlined style={{ fontSize: "10px", color: "#9DA2AF", marginRight: "6px", marginLeft: "6px" }} />
            {kindName || '選擇車款'}
          </span>}
          {!!kindName && <span className="min-w-[80px]">
            <RightOutlined style={{ fontSize: "10px", color: "#9DA2AF", marginRight: "6px", marginLeft: "6px" }} />
            選擇車型
          </span>}
        </p>

        {brandId === undefined &&
          <BrandSelect
            brandData={brandData as IBrand}
            handleClickBrandId={(id) => {
              setBrandId(id);
              setInitStatus(false);
            }}
            handleClickBrandName={(name) => {
              setBranamedName(name);
            }}
          />}

        {(!!brandId && kindId === undefined) &&
          <KindSelect
            kindData={kindData as ICommonKindData[]}
            handleClickKind={(id) => {
              setKindId(id);
              setInitStatus(false);
            }}
            handleClickKindName={(name) => {
              setKindName(name);
            }}
          />}

        {!!kindId &&
          <ModelSelect
            modelData={modelData as IModel[]}
            disabledModels={disabledModels || []}
            handleClickModel={(data) => {
              setInitStatus(false);
              setCompeteListValue({
                brandId: Number(brandId),
                brandName: brandName,
                kindId: Number(kindId),
                kindName: kindName,
                modelName: data.modelName,
                myid: data.myid,
                thumb: data.thumb,
                price: data.price,
              });
            }}
          />
        }
      </div>}
    </section>
  );
};
export default BrandKindModelSelect;
